/**
 * Templates & Styles — the catalogue a coordinator is offered.
 *
 * Three tabs over one table: styles, editions (the PDF bindings) and dashboard
 * layouts. Rename, re-describe, reorder, retire, change tier availability, and
 * create new items.
 *
 * ⚠️ WHAT THIS PAGE MUST NOT IMPLY.
 * Creating an item makes it OFFERED. It does not make it LOOK like anything.
 * A new edition's ornament, a new layout's grid and a new style's cover
 * treatment are CSS and JSX compiled into two container images; an item created
 * here without a stylesheet renders as the unornamented default. That is stated
 * beside the create button rather than left for someone to discover in a
 * finished keepsake — which is the whole reason this page carries so much prose.
 *
 * ⚠️ AND WHAT IS NOT LIVE YET.
 * The wedding services still read their compiled-in constants; the code that
 * makes them read these rows lives on the wedding_book branch and has not
 * shipped. Until it does, edits here are recorded and audited but do NOT change
 * what a coordinator sees. The banner says so. Removing that banner before the
 * service ships would make this page a convincing lie.
 */

import React from 'react';
import {
  Box, Typography, Stack, Tabs, Tab, Button, Chip, Tooltip, Alert, AlertTitle,
  Snackbar, IconButton, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaletteIcon from '@mui/icons-material/Palette';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import EmptyState from '../../common/EmptyState';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { CatalogueItem, CatalogueResponse, CatalogueKind } from '../weddingApi';
import { num } from '../weddingFormat';
import CatalogueItemDialog from './CatalogueItemDialog';

const KINDS: { key: CatalogueKind; label: string; blurb: string }[] = [
  {
    key: 'style',
    label: 'Styles',
    blurb: 'The palette and typographic feel of the finished book. Three colours; the rest of the palette is derived at render time against a contrast ladder.',
  },
  {
    key: 'edition',
    label: 'Editions',
    blurb: 'The binding — ornament, rules and crest. Each edition is a print stylesheet; an edition without one renders unornamented.',
  },
  {
    key: 'layout',
    label: 'Dashboard layouts',
    blurb: 'How the coordinator dashboard arranges itself. Offered to every tier — layouts are deliberately not gated.',
  },
];

const WeddingCataloguePage: React.FC = () => {
  const [tab, setTab] = React.useState(0);
  const [data, setData] = React.useState<CatalogueResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showInactive, setShowInactive] = React.useState(true);
  const [editing, setEditing] = React.useState<CatalogueItem | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [toast, setToast] = React.useState<{ severity: 'success' | 'error' | 'warning'; message: string } | null>(null);
  // The un-saved arrangement. Null means "showing the server's order"; a list
  // means the operator has moved something and nothing has been sent yet.
  const [pendingOrder, setPendingOrder] = React.useState<string[] | null>(null);
  const [confirmOrder, setConfirmOrder] = React.useState(false);
  const [savingOrder, setSavingOrder] = React.useState(false);

  const kind = KINDS[tab].key;

  /**
   * ALWAYS fetch the retired items too, and filter for display client-side.
   *
   * Two writes on this page rebuild a whole list from what the client holds —
   * a tier PUT replaces that tier's entire grant list, and reorder rewrites
   * every sort_order for the kind. Fetching a filtered catalogue and then
   * sending the result back would silently revoke retired items' grants and
   * strand their sort_order. "Show retired" is a view, not a query.
   */
  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await weddingApi.getCatalogue({ include_inactive: true }));
      setPendingOrder(null);
    } catch (e) {
      setError((e as WeddingApiError).message || 'Could not load the catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Every item of this kind, retired ones included — what the two list-rewriting
  // mutations must be built from.
  const kindItems = React.useMemo(
    () => (data ? data.items : []).filter((i) => i.kind === kind),
    [data, kind]
  );

  // The order on screen: the operator's un-saved arrangement if there is one,
  // otherwise the server's (it returns rows sorted by sort_order).
  const orderedItems = React.useMemo(() => {
    if (!pendingOrder) return kindItems;
    const byKey = new Map(kindItems.map((i) => [i.item_key, i]));
    const out = pendingOrder.map((k) => byKey.get(k)).filter((i): i is CatalogueItem => !!i);
    // Anything the pending order does not name (a reload added a row) keeps
    // its server position at the end rather than disappearing.
    for (const i of kindItems) if (!pendingOrder.includes(i.item_key)) out.push(i);
    return out;
  }, [kindItems, pendingOrder]);

  const rows = orderedItems.filter((i) => showInactive || i.is_active);

  /** Move one item one place in the FULL order for this kind. */
  const move = (itemKey: string, delta: -1 | 1) => {
    const keys = orderedItems.map((i) => i.item_key);
    const from = keys.indexOf(itemKey);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= keys.length) return;
    const next = [...keys];
    [next[from], next[to]] = [next[to], next[from]];
    setPendingOrder(next);
  };

  /**
   * Why reordering is refused while retired items are hidden.
   *
   * `reorderCatalogue` rewrites sort_order for EVERY key it is given and
   * leaves out anything it is not. Moving a row while some rows are invisible
   * would either strand the hidden ones' sort_order or quietly shove them to
   * the end — neither of which the operator asked for.
   */
  const reorderBlocked = showInactive
    ? null
    : 'Turn on “Show retired” to reorder — the saved order covers every item, retired ones included.';

  const saveOrder = async () => {
    if (!pendingOrder) return;
    setSavingOrder(true);
    try {
      const r = await weddingApi.reorderCatalogue(kind, pendingOrder);
      setConfirmOrder(false);
      setToast({ severity: 'success', message: `Order saved — ${num(r.ordered)} ${KINDS[tab].label.toLowerCase()} renumbered.` });
      await load();
    } catch (e) {
      setConfirmOrder(false);
      setToast({ severity: 'error', message: (e as WeddingApiError).message || 'Could not save the order' });
    } finally {
      setSavingOrder(false);
    }
  };

  const columns: GridColDef<CatalogueItem>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => (
        <Box sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2">{p.row.name}</Typography>
            {p.row.is_builtin && (
              <Tooltip title="Shipped with the product. Can be renamed or retired, never deleted.">
                <Chip size="small" variant="outlined" label="built-in" />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {p.row.item_key}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Offered',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => (
        <Chip
          size="small"
          variant="outlined"
          color={p.row.is_active ? 'success' : 'default'}
          label={p.row.is_active ? 'Yes' : 'Retired'}
        />
      ),
    },
    {
      field: 'tiers',
      headerName: 'Tiers',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => (
        p.row.kind === 'layout'
          ? <Typography variant="body2" color="text.secondary">all tiers</Typography>
          : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(p.row.tiers || []).map((t) => (
                <Chip key={t} size="small" variant="outlined" label={t} />
              ))}
            </Stack>
          )
      ),
    },
    {
      field: 'books_using',
      headerName: 'In use',
      width: 100,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => (
        <Typography variant="body2">
          {num(p.row.kind === 'layout' ? p.row.couples_using : p.row.books_using)}
        </Typography>
      ),
    },
    {
      field: 'print_css',
      headerName: 'Appearance',
      width: 190,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => {
        if (p.row.is_builtin) {
          return (
            <Tooltip title="Ships inside the pdf image and the web bundle.">
              <Typography variant="body2" color="text.secondary">compiled in</Typography>
            </Tooltip>
          );
        }
        const has = p.row.kind === 'edition' ? !!p.row.print_css : !!p.row.web_css;
        return has
          ? <Chip size="small" variant="outlined" color="success" label="custom CSS" />
          : (
            <Tooltip title="Without a stylesheet this renders as the unornamented default.">
              <Chip size="small" variant="outlined" color="warning" label="no stylesheet" />
            </Tooltip>
          );
      },
    },
    {
      field: 'actions',
      headerName: 'Order / edit',
      width: 140,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<CatalogueItem>) => {
        const position = orderedItems.findIndex((i) => i.item_key === p.row.item_key);
        return (
          <Stack direction="row" spacing={0} alignItems="center">
            <Tooltip title={reorderBlocked || 'Move up'}>
              <span>
                <IconButton
                  size="small" aria-label={`Move ${p.row.name} up`}
                  disabled={!!reorderBlocked || position <= 0}
                  onClick={() => move(p.row.item_key, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={reorderBlocked || 'Move down'}>
              <span>
                <IconButton
                  size="small" aria-label={`Move ${p.row.name} down`}
                  disabled={!!reorderBlocked || position < 0 || position >= orderedItems.length - 1}
                  onClick={() => move(p.row.item_key, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" aria-label={`Edit ${p.row.name}`} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <PaletteIcon color="primary" />
        <Typography variant="h5">Templates &amp; Styles</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label="Show retired"
        />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
        <AlertTitle>Recorded, but not yet live</AlertTitle>
        The wedding services still read their built-in catalogue. Changes here
        are saved and audited, but a coordinator will not see them until the
        service-side release ships. Nothing on this page can break an existing
        book.
      </Alert>

      {/* An un-saved order belongs to ONE kind — carrying it across tabs would
          apply Styles' arrangement to Editions' keys. */}
      <Tabs value={tab} onChange={(_e, v) => { setTab(v); setPendingOrder(null); }} sx={{ mb: 1 }}>
        {KINDS.map((k) => <Tab key={k.key} label={k.label} />)}
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {KINDS[tab].blurb}
      </Typography>

      {error && !loading && !data ? (
        <EmptyState
          icon="error"
          title="Could not load the catalogue"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : (
        <>
          {pendingOrder && (
            <Alert
              severity="info"
              sx={{ mb: 1 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setPendingOrder(null)} disabled={savingOrder}>
                    Discard
                  </Button>
                  <Button size="small" variant="contained" onClick={() => setConfirmOrder(true)} disabled={savingOrder}>
                    Save order
                  </Button>
                </Stack>
              }
            >
              This order has not been saved. Nothing has been sent yet.
            </Alert>
          )}

          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r.id}
            hideFooter
            disableRowSelectionOnClick
            autoHeight
          />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => setCreating(true)}>
              Add a {KINDS[tab].label.replace(/s$/, '').toLowerCase()}
            </Button>
            <Typography variant="caption" color="text.secondary">
              A new item is offered immediately, but its <strong>appearance</strong> is
              code: without a stylesheet it renders as the unornamented default,
              and picker thumbnails and layout diagrams need a release either way.
            </Typography>
          </Stack>
        </>
      )}

      {/* Reorder confirm — the same gate every other wedding mutation is
          behind. No request until this is confirmed. */}
      <Dialog open={confirmOrder} onClose={savingOrder ? undefined : () => setConfirmOrder(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save this order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {KINDS[tab].label} will be renumbered in the order shown, and coordinators
            will be offered them in that order once the service-side release ships.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOrder(false)} disabled={savingOrder}>Cancel</Button>
          <Button
            variant="contained" onClick={saveOrder} disabled={savingOrder}
            startIcon={savingOrder ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save order
          </Button>
        </DialogActions>
      </Dialog>

      <CatalogueItemDialog
        open={editing !== null || creating}
        kind={kind}
        item={editing}
        items={kindItems}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={(msg) => {
          setToast({ severity: 'success', message: msg });
          setEditing(null);
          setCreating(false);
          load();
        }}
        onError={(msg) => setToast({ severity: 'error', message: msg })}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WeddingCataloguePage;
